import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import DecoratedInput from '@/Components/Input/DecoratedInput';
import Modal from '@/Components/Modal/Modal';
import { MutuallyExclusiveMediaQueryBreakpoints, useMediaQuery } from '@/Hooks/useMediaQuery';
import { useApplication } from '@/Components/ApplicationProvider';
import { c } from 'ttag';
const U2FAddDeviceView = ({ addAuthenticator, onDeviceAddingModalToggle, onDeviceAdded }) => {
    const application = useApplication();
    const [deviceName, setDeviceName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [registrationOptions, setRegistrationOptions] = useState(null);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    useEffect(() => {
        let isActive = true;
        void application.fetchAuthenticatorRegistrationOptions().then((options) => {
            if (!isActive) {
                return;
            }
            setRegistrationOptions(options);
            setIsLoadingOptions(false);
            if (options === null) {
                setErrorMessage(c('B6.Preferences.Security.Error').t `Could not prepare security key registration`);
            }
        });
        return () => {
            isActive = false;
        };
    }, [application]);
    const handleDeviceNameChange = useCallback((deviceName) => {
        setDeviceName(deviceName);
    }, []);
    const handleAddDeviceClick = useCallback(async () => {
        if (!deviceName) {
            setErrorMessage(c('B6.Preferences.Security.Error').t `Device name is required`);
            return;
        }
        if (!registrationOptions) {
            setErrorMessage(c('B6.Preferences.Security.Error').t `Could not prepare security key registration`);
            return;
        }
        const user = application.sessions.getUser();
        if (user === undefined) {
            setErrorMessage(c('B6.Preferences.Security.Error').t `User not found`);
            return;
        }
        const authenticatorAddedOrError = await addAuthenticator.execute({
            userUuid: user.uuid,
            authenticatorName: deviceName,
            registrationOptions,
        });
        if (authenticatorAddedOrError.isFailed()) {
            setErrorMessage(authenticatorAddedOrError.getError());
            return;
        }
        onDeviceAddingModalToggle(false);
        await onDeviceAdded();
    }, [
        deviceName,
        registrationOptions,
        setErrorMessage,
        application,
        addAuthenticator,
        onDeviceAddingModalToggle,
        onDeviceAdded,
    ]);
    const closeModal = () => {
        onDeviceAddingModalToggle(false);
    };
    const isMobileScreen = useMediaQuery(MutuallyExclusiveMediaQueryBreakpoints.sm);
    const addDeviceActionLabel = isMobileScreen
        ? c('B6.Preferences.Security.Action').t `Add`
        : c('B6.Preferences.Security.Action').t `Add Device`;
    return (_jsx(Modal, { title: c('B6.Preferences.Security.Title').t `Add Security Key`, close: closeModal, actions: [
            {
                label: c('B6.Preferences.Security.Action').t `Cancel`,
                type: 'cancel',
                onClick: closeModal,
                mobileSlot: 'left',
                hidden: !isMobileScreen,
            },
            {
                label: addDeviceActionLabel,
                type: 'primary',
                onClick: handleAddDeviceClick,
                mobileSlot: 'right',
                disabled: isLoadingOptions || registrationOptions === null,
            },
        ], children: _jsx("div", { className: "flex px-4 py-4", children: _jsxs("div", { className: "ml-4 flex flex-grow flex-col gap-1", children: [_jsx("label", { htmlFor: "u2f-device-name", className: "mb-2 text-sm font-semibold", children: c('B6.Preferences.Security.Label').t `Device Name` }), _jsx(DecoratedInput, { autofocus: true, id: "u2f-device-name", className: { container: 'w-92' }, value: deviceName, onChange: handleDeviceNameChange, onEnter: handleAddDeviceClick }), errorMessage && _jsx("div", { className: "mt-1.5 text-danger", children: errorMessage })] }) }) }));
};
export default observer(U2FAddDeviceView);
